import { Button, Group, Title } from "@mantine/core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  MantineReactTable,
  type MRT_ColumnDef,
  type MRT_RowSelectionState,
  useMantineReactTable
} from "mantine-react-table-open";
import { type ReactNode, useMemo, useState } from "react";
import api from "../../../api";
import { useAdvancePayments } from "../../../hooks/useAdvancePayments";
import { useSites } from "../../../hooks/useSites";
import { getDefaultMRTOptions } from "../../../mantine/getDefaultMRTOptions";
import { useToast } from "../../../mantine/notifications/useToast";
import urls from "../../../urls";
import { postDataAndPollResponse } from "../../../utils/api-utils";
import type {
  AdvancePaymentsPlanData,
  AdvancePaymentsPlan as AdvancePaymentsPlanResponse,
  Person,
  Site
} from "../../../utils/backend-types";
import { DeleteConfirmationModal } from "../../BuildingBlocks/Layout/Modals/DeleteConfirmationModal/DeleteConfirmationModal";
import type { Contract } from "../../DeliveryOverview/DeliveryOverview.types";
import { LoadOrError } from "../../LoadOrError/LoadOrError";
import { FileCell } from "../columns/FileCell";
import { getDateRange } from "../getDateRange";
import { AdvancePaymentsPlansControls } from "./AdvancePaymentsControls";

const defaultSorted = [
  {
    id: "billingPeriodStart",
    desc: true
  }
];

type AdvancePaymentsPlan = Omit<
  AdvancePaymentsPlanResponse,
  "advancePaymentsPlanData"
> &
  Partial<Omit<AdvancePaymentsPlanData, "id">> & {
    supplied?: number;
    supplier?: number;
  };

interface AdvancePaymentsPlansProps {
  advancePaymentsPlans: Array<AdvancePaymentsPlan>;
  persons: Array<Person>;
  sites: Array<Site>;
  selection: MRT_RowSelectionState;
  topToolbarActions?: ReactNode;
  onSelectionChange: (selection: MRT_RowSelectionState) => void;
}

function AdvancePaymentsPlans({
  advancePaymentsPlans,
  persons,
  selection,
  sites,
  topToolbarActions,
  onSelectionChange
}: AdvancePaymentsPlansProps) {
  const columns: Array<MRT_ColumnDef<AdvancePaymentsPlan>> = [
    {
      header: "Abschlagsplan",
      accessorKey: "id",
      accessorFn: (row) => {
        if (
          !row.paymentPlanBegin ||
          !row.paymentPlanEnd ||
          !row.supplied ||
          !row.supplier
        ) {
          return "Unbekannt";
        }

        const supplierName =
          persons.find((c) => c.id === row.supplier)?.name ??
          "<Kein_Lieferant>";
        const suppliedName =
          persons.find((c) => c.id === row.supplied)?.name ??
          "<Kein_Belieferter>";
        const billingPeriod =
          row.paymentPlanBegin || "<Kein_Abrechnungsbeginn>";

        return `Abschlag_${supplierName}_${suppliedName}_${billingPeriod}`;
      },
      size: 300,
      Cell: ({ row }) =>
        row.original.paymentPlanBegin &&
        row.original.paymentPlanEnd &&
        row.original.supplied &&
        row.original.supplier ? (
          <FileCell
            billingPeriodStart={row.original.paymentPlanBegin}
            persons={persons}
            prefix="Abschlag"
            suppliedId={row.original.supplied}
            supplierId={row.original.supplier}
            url={urls.api.downloadAdvancePaymentsPlan(row.original.id)}
          />
        ) : (
          "Unbekannt"
        )
    },
    {
      header: "Lieferant",
      accessorKey: "supplier",
      accessorFn: (row) =>
        persons.find((c) => c.id === row.supplier)?.name ?? "Unbekannt"
    },
    {
      header: "Belieferter",
      accessorKey: "supplied",
      accessorFn: (row) =>
        persons.find((c) => c.id === row.supplied)?.name ?? "Unbekannt"
    },
    {
      header: "Liegenschaft",
      accessorKey: "site",
      accessorFn: (row) =>
        sites.find((s) => s.id === row.site)?.name ?? "Unbekannt"
    },
    {
      header: "Abrechnungszeitraum",
      accessorKey: "billingPeriodStart",
      Cell: ({ row }) =>
        getDateRange({
          beginDate: row.original.paymentPlanBegin,
          endDate: row.original.paymentPlanEnd
        })
    }
  ];

  const table = useMantineReactTable<AdvancePaymentsPlan>({
    ...getDefaultMRTOptions({
      initialState: { sorting: defaultSorted, showGlobalFilter: true },
      emptyRowsFallbackText: "Keine Abschläge vorhanden",
      hideBorder: true
    }),
    state: { rowSelection: selection },
    onRowSelectionChange: (newSelection) => {
      if (typeof newSelection === "function") {
        onSelectionChange(newSelection(selection));
      }
    },
    enableGlobalFilter: true,
    enableFilterMatchHighlighting: false,
    globalFilterFn: "includesString",
    enableRowSelection: true,
    positionToolbarAlertBanner: "none",
    enableColumnResizing: true,
    columns,
    data: advancePaymentsPlans,
    renderTopToolbarCustomActions: topToolbarActions
      ? () => (
          <Group justify="flex-end" p={0} w="stretch">
            {topToolbarActions}
          </Group>
        )
      : undefined
  });

  return <MantineReactTable table={table} />;
}

interface AdvancePaymentsDataLoader {
  projectId: string;
  contracts: Array<Contract>;
  persons: Array<Person>;
}

function AdvancePaymentsPlansDataLoader({
  projectId,
  contracts,
  persons
}: AdvancePaymentsDataLoader) {
  const { showToast, showErrorToast } = useToast();
  const {
    data: sites,
    isLoading: isLoadingSites,
    error: sitesError
  } = useSites({ projectId });

  const {
    data: advancePaymentsPlansResponse,
    isLoading: advancePaymentsPlanResponseLoading,
    error: advancePaymentsPlanResponseError
  } = useAdvancePayments(projectId);

  const {
    data: archivedAdvancePaymentsPlansResponse,
    isLoading: archivedAdvancePaymentsPlanResponseLoading,
    error: archivedAdvancePaymentsPlanResponseError
  } = useQuery({
    queryKey: ["archivedAdvancePaymentsPlans", { projectId }],
    queryFn: () => fetchArchivedAdvancePayments(projectId)
  });
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isArchiveLoading, setIsArchiveLoading] = useState(false);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const queryClient = useQueryClient();

  const [advancePaymentPlanSelection, setAdvancePaymentPlanSelection] =
    useState<MRT_RowSelectionState>({});
  const [
    archivedAdvancePaymentPlanSelection,
    setArchivedAdvancePaymentPlanSelection
  ] = useState<MRT_RowSelectionState>({});

  function getSelectedAdvancePaymentsPlans(): Array<AdvancePaymentsPlan> {
    const selectedIds = Object.keys(advancePaymentPlanSelection).filter(
      (key) => advancePaymentPlanSelection[key]
    );
    return (
      advancePaymentsPlans?.filter((plan) => selectedIds.includes(plan.id)) ||
      []
    );
  }

  function getSelectedArchivedAdvancePaymentsPlans(): Array<AdvancePaymentsPlan> {
    const selectedIds = Object.keys(archivedAdvancePaymentPlanSelection).filter(
      (key) => archivedAdvancePaymentPlanSelection[key]
    );
    return (
      archivedAdvancePaymentsPlans?.filter((plan) =>
        selectedIds.includes(plan.id)
      ) || []
    );
  }

  const advancePaymentsPlans = useMemo(() => {
    if (advancePaymentsPlansResponse) {
      const newAdvancePaymentPlans: Array<AdvancePaymentsPlan> =
        advancePaymentsPlansResponse.map((advancePaymentsPlanResponse) => {
          const {
            advancePaymentsPlanData,
            ...advancePaymentsPlanWithoutDataId
          } = advancePaymentsPlanResponse;
          const { id, ...advancePaymentsPlanDataWithoutId } =
            advancePaymentsPlanData;
          const contract = contracts.find(
            (contract) => contract.id === advancePaymentsPlanData.contract
          );

          return {
            ...advancePaymentsPlanWithoutDataId,
            ...advancePaymentsPlanDataWithoutId,
            supplier: contract ? contract.supplier : undefined,
            supplied: contract ? contract.supplied : undefined
          };
        });

      return newAdvancePaymentPlans;
    }
  }, [advancePaymentsPlansResponse, contracts]);

  const archivedAdvancePaymentsPlans = useMemo(() => {
    if (archivedAdvancePaymentsPlansResponse) {
      const newAdvancePaymentPlans: Array<AdvancePaymentsPlan> =
        archivedAdvancePaymentsPlansResponse.map(
          (advancePaymentsPlanResponse) => {
            const {
              advancePaymentsPlanData,
              ...advancePaymentsPlanWithoutDataId
            } = advancePaymentsPlanResponse;
            const { id, ...advancePaymentsPlanDataWithoutId } =
              advancePaymentsPlanData;
            const contract = contracts.find(
              (contract) => contract.id === advancePaymentsPlanData.contract
            );

            return {
              ...advancePaymentsPlanWithoutDataId,
              ...advancePaymentsPlanDataWithoutId,
              supplier: contract ? contract.supplier : undefined,
              supplied: contract ? contract.supplied : undefined
            };
          }
        );

      return newAdvancePaymentPlans;
    }
  }, [archivedAdvancePaymentsPlansResponse, contracts]);

  async function handleGenerateAdvancePaymentsPlans(year: number) {
    const postUrl = urls.api.generateAdvancePaymentsPlans(projectId, year);

    return postDataAndPollResponse(postUrl, {})
      .then(() => {
        showToast({
          type: "success",
          message: "Alle angeforderten Abschlagspläne wurden erstellt."
        });
      })
      .catch((error) => {
        showErrorToast(error);
      })
      .finally(() => {
        queryClient.invalidateQueries({
          queryKey: [
            "advancePaymentsPlans",
            {
              projectId
            }
          ]
        });
      });
  }

  function handleClickArchiveAdvancePaymentsPlans() {
    if (!archivedAdvancePaymentsPlans) {
      return;
    }

    setIsArchiveLoading(true);

    const archivePromises = getSelectedAdvancePaymentsPlans().map((item) => {
      const url = urls.api.archiveAdvancePaymentsPlan(item.id);
      return api.post(url);
    });

    let success = false;
    Promise.all(archivePromises)
      .then(() => {
        success = true;
      })
      .catch((error) => {
        showErrorToast(error);
      })
      .finally(() => {
        if (success) {
          queryClient.invalidateQueries({
            queryKey: [
              "advancePaymentsPlans",
              {
                projectId
              }
            ]
          });
          queryClient.invalidateQueries({
            queryKey: [
              "archivedAdvancePaymentsPlans",
              {
                projectId
              }
            ]
          });
          setAdvancePaymentPlanSelection({});
        }

        setIsArchiveLoading(false);
      });
  }

  function handleDeleteAdvancePaymentsPlans() {
    if (!advancePaymentsPlans) {
      return;
    }

    setIsDeleteLoading(true);

    const deletePromises = getSelectedArchivedAdvancePaymentsPlans().map(
      (item) => {
        const url = urls.api.advancePaymentsPlan(item.id);
        return api.delete(url);
      }
    );

    let success = false;
    Promise.all(deletePromises)
      .then(() => {
        success = true;
      })
      .catch((error) => {
        showErrorToast(error);
      })
      .finally(() => {
        if (success) {
          queryClient.invalidateQueries({
            queryKey: [
              "archivedAdvancePaymentsPlans",
              {
                projectId
              }
            ]
          });
          setArchivedAdvancePaymentPlanSelection({});
        }

        setIsDeleteLoading(false);
        setDeleteModalOpen(false);
      });
  }

  function getDeleteConfirmationModalText() {
    const numberOfSelected = getSelectedArchivedAdvancePaymentsPlans().length;
    if (numberOfSelected === 1) {
      return `diesen Abschlagsplan`;
    } else {
      return `diese ${getSelectedArchivedAdvancePaymentsPlans().length} Abschlagspläne`;
    }
  }

  return (
    <LoadOrError
      error={
        advancePaymentsPlanResponseError ||
        archivedAdvancePaymentsPlanResponseError ||
        sitesError
      }
      loading={
        advancePaymentsPlanResponseLoading ||
        archivedAdvancePaymentsPlanResponseLoading ||
        isLoadingSites
      }
    >
      {advancePaymentsPlans && archivedAdvancePaymentsPlans && sites && (
        <div className="AdvancePayments">
          <Title fw={500} mb="xs" order={5}>
            Aktive Abschlagspläne
          </Title>
          <AdvancePaymentsPlans
            advancePaymentsPlans={advancePaymentsPlans}
            persons={persons}
            selection={advancePaymentPlanSelection}
            sites={sites}
            topToolbarActions={
              <AdvancePaymentsPlansControls
                isArchiveDisabled={
                  getSelectedAdvancePaymentsPlans().length === 0
                }
                isArchiveLoading={isArchiveLoading}
                onArchive={handleClickArchiveAdvancePaymentsPlans}
                onGenerate={handleGenerateAdvancePaymentsPlans}
              />
            }
            onSelectionChange={setAdvancePaymentPlanSelection}
          />
          <Title fw={500} mb="xs" mt="xl" order={5}>
            Archivierte Abschlagspläne
          </Title>
          <AdvancePaymentsPlans
            advancePaymentsPlans={archivedAdvancePaymentsPlans}
            persons={persons}
            selection={archivedAdvancePaymentPlanSelection}
            sites={sites}
            topToolbarActions={
              <Group gap="xs">
                <Button
                  className="delete-button"
                  color="error"
                  disabled={
                    getSelectedArchivedAdvancePaymentsPlans().length === 0
                  }
                  onClick={() => setDeleteModalOpen(true)}
                >
                  Löschen
                </Button>
                <DeleteConfirmationModal
                  isLoading={isDeleteLoading}
                  name={getDeleteConfirmationModalText()}
                  opened={deleteModalOpen}
                  toggleModal={() => {
                    setDeleteModalOpen(!deleteModalOpen);
                  }}
                  onConfirm={handleDeleteAdvancePaymentsPlans}
                />
              </Group>
            }
            onSelectionChange={setArchivedAdvancePaymentPlanSelection}
          />
        </div>
      )}
    </LoadOrError>
  );
}

async function fetchArchivedAdvancePayments(
  projectId: string
): Promise<Array<AdvancePaymentsPlanResponse>> {
  const advancePaymentsPlansUrl =
    urls.api.archivedAdvancePaymentsPlans(projectId);
  const response = await api.get<Array<AdvancePaymentsPlanResponse>>(
    advancePaymentsPlansUrl
  );

  return response.data;
}

export { AdvancePaymentsPlansDataLoader as AdvancePaymentsPlans };
